import { z } from 'zod';

import { AccountRefSchema } from '../../users/account-ref/account-ref';

import { TestAttemptSchema } from '../test-attempt/test-attempt';

/**
 * An attempts-viewer row: the attempt, the member who sat it, and the test.
 *
 * Both added fields are page-batched lookups rather than columns, which is why
 * they live on an extension instead of on {@link TestAttemptSchema} — the base
 * stays the attempt as the repository holds it, and this is the attempt as a
 * support queue has to read it (`DiscountGrantDetailSchema`'s reasoning, and
 * `ReportDetailSchema`'s before it).
 *
 * **`member` is `null` only when the lookup came back short.** `user_id` is
 * `notNull`, so there is no domain reading of an absent member; it is a
 * hard-deleted account, or a users module answering for fewer ids than were
 * asked. The row is still an attempt an operator may need to reset.
 *
 * **`testTitle` is nullable for the same reason and NOT for `courseTitle`'s.**
 * On `CourseTestSchema` a `null` title means "standalone — there is no course",
 * a fact about the domain. Here `test_id` is a notNull FK, so an attempt always
 * has a test: `null` can only mean the title lookup came back short — a test
 * soft-deleted since the attempt was sat, or one with no translation in the
 * reader's locale. The two ways of having no title mean opposite things, and
 * this one has only the second.
 *
 * It is `null` rather than `''` — which is what `ruleName` degrades to on the
 * grant row — because the reset confirmation names the test it is clearing, and
 * a blank name there reads as a finished sentence with a word missing rather
 * than as a fact the platform could not supply.
 */
export const TestAttemptDetailSchema = TestAttemptSchema.extend({
  member: AccountRefSchema.nullable(),
  testTitle: z.string().nullable(),
}).strict();

export type TestAttemptDetail = z.infer<typeof TestAttemptDetailSchema>;
